import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, CheckCircle2, ReceiptText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' · ' + d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }) + ' Uhr';
  } catch { return iso; }
};
const fmtChf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;

export default function BillingAuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState('90');
  const [loading, setLoading] = useState(true);
  const [rebilling, setRebilling] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmFixPM, setConfirmFixPM] = useState(false);
  const [fixingPM, setFixingPM] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [dupLoading, setDupLoading] = useState(true);

  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/billing-audit/unbilled?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total_amount || 0);
    } catch (e) {
      toast.error('Fehler beim Laden der Abrechnungs-Kontrolle');
    } finally {
      setLoading(false);
    }
  }, [days, token]);

  useEffect(() => { load(); }, [load]);

  const loadDupes = useCallback(async () => {
    setDupLoading(true);
    try {
      const res = await axios.get(`${API}/billing/duplicate-invoice-check`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDupes(res.data.duplicates || []);
    } catch (e) {
      // still fine to show empty
    } finally {
      setDupLoading(false);
    }
  }, [token]);

  useEffect(() => { loadDupes(); }, [loadDupes]);

  const runFixPaymentMethods = async () => {
    setFixingPM(true);
    try {
      const res = await axios.post(`${API}/admin/billing-audit/fix-payment-methods?dry_run=false`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.fixed} Zahlungsart(en) auf Karte/TWINT korrigiert`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Korrektur fehlgeschlagen');
    } finally {
      setFixingPM(false);
      setConfirmFixPM(false);
    }
  };

  const rebill = async (jobs) => {
    if (!jobs.length) return;
    setRebilling(true);
    try {
      const res = await axios.post(`${API}/admin/billing-audit/rebill`,
        { items: jobs },
        { headers: { Authorization: `Bearer ${token}` } });
      const billed = res.data.billed || 0;
      if (billed > 0) {
        toast.success(`${billed} Training(s) korrekt abgerechnet — Rechnung(en) erstellt.`);
      } else {
        const firstErr = (res.data.results || []).find(r => !r.success)?.error;
        toast.error(firstErr || 'Es konnte keine Rechnung erstellt werden.');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nachverrechnung fehlgeschlagen');
    } finally {
      setRebilling(false);
      setConfirmAll(false);
    }
  };

  const deleteRows = async (ids) => {
    if (!ids.length) return;
    setRebilling(true);
    try {
      const res = await axios.post(`${API}/admin/billing-audit/delete`,
        { appointment_ids: ids },
        { headers: { Authorization: `Bearer ${token}` } });
      if ((res.data.deleted || 0) > 0) {
        toast.success(`${res.data.deleted} toter Termin(e) gelöscht.`);
      } else {
        toast.error('Nichts gelöscht (Termin hat evtl. eine echte Abrechnung).');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setRebilling(false);
      setConfirmDelete(null);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="billing-audit-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            Abrechnungs-Kontrolle
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Regel: <strong>Jedes Training</strong> muss entweder einen Punkte-Abzug,
            eine Zahlung <em>oder</em> eine Rechnung haben. Hier erscheinen vergangene
            Trainings, bei denen <strong>nichts davon</strong> vorhanden ist. Termine von
            <strong>heute</strong> erscheinen erst nach dem nächtlichen Abrechnungslauf; Ende-Monat-Kunden
            erst, wenn der Monat vorbei ist und trotzdem keine Rechnung erzeugt wurde.
            Der Admin wird bei neuen Fällen automatisch benachrichtigt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px]" data-testid="billing-audit-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Letzte 30 Tage</SelectItem>
              <SelectItem value="90">Letzte 90 Tage</SelectItem>
              <SelectItem value="180">Letzte 180 Tage</SelectItem>
              <SelectItem value="3650">Alle</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="billing-audit-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
          </Button>
          <Button variant="outline" onClick={() => setConfirmFixPM(true)} disabled={fixingPM} data-testid="billing-audit-fix-pm">
            <ReceiptText className="h-4 w-4 mr-1" /> Zahlungsarten korrigieren
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmFixPM} onOpenChange={setConfirmFixPM}>
        <AlertDialogContent data-testid="billing-audit-fix-pm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Zahlungsarten korrigieren</AlertDialogTitle>
            <AlertDialogDescription>
              Korrigiert Zahlungen, die noch als „rechnung" markiert sind, obwohl die
              Rechnung per Payrexx (Karte/TWINT) bezahlt wurde. Nur die falsch beschriftete
              Zahlungsart wird angepasst — keine Beträge, keine neuen Belastungen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-audit-fix-pm-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={runFixPaymentMethods} disabled={fixingPM} data-testid="billing-audit-fix-pm-confirm">
              {fixingPM ? 'Korrigiere…' : 'Jetzt korrigieren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Doppelverrechnungs-Prüfung */}
      <Card className="mb-4 border-red-200" data-testid="duplicate-invoice-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              {dupLoading ? 'Doppelverrechnungen werden geprüft…' : `${dupes.length} mögliche Doppelverrechnung(en)`}
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadDupes} disabled={dupLoading} data-testid="duplicate-invoice-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${dupLoading ? 'animate-spin' : ''}`} /> Aktualisieren
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Termine, für die derselbe Kunde MEHR ALS EINE nicht-stornierte Rechnung hat.
            Gruppen-/Duo-/1:3-Trainings (mehrere Teilnehmer im selben Slot) werden korrekt
            NICHT gezählt. Bereinigung: offene Dublette „Stornieren", bezahlte Dublette rückerstatten/gutschreiben.
          </p>
        </CardHeader>
        <CardContent>
          {!dupLoading && dupes.length === 0 && (
            <p className="text-sm text-green-700" data-testid="duplicate-invoice-none">✅ Keine Doppelverrechnungen gefunden.</p>
          )}
          {dupes.length > 0 && (
            <div className="space-y-2" data-testid="duplicate-invoice-list">
              {dupes.map((d, i) => (
                <div key={d.appointment_id} className="p-3 rounded-lg bg-red-50 border border-red-200" data-testid={`duplicate-invoice-row-${i}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm font-semibold text-gray-900">
                      {d.customer_name || 'Kunde'} · {d.date ? new Date(d.date).toLocaleDateString('de-CH') : '—'} · {d.service_name}
                    </div>
                    <span className="text-xs font-medium text-red-700">{d.recommendation}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {d.invoices.map((inv) => (
                      <span key={inv.id} className="inline-flex items-center gap-1 text-xs bg-white border border-red-200 rounded px-2 py-0.5" data-testid={`duplicate-invoice-badge-${inv.invoice_number}`}>
                        <strong>{inv.invoice_number}</strong>
                        <span className={inv.status === 'paid' ? 'text-green-600' : 'text-amber-600'}>
                          {inv.status === 'paid' ? 'bezahlt' : (inv.status || 'offen')}
                        </span>
                        {inv.total != null && <span className="text-gray-500">CHF {Number(inv.total).toFixed(2)}</span>}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>


      <Card className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg">
              {loading ? 'Wird geprüft…' : `${items.length} Training(s) ohne Abrechnung`}
              {!loading && items.length > 0 && (
                <span className="ml-2 text-amber-600 font-bold">· {fmtChf(total)} nicht verrechnet</span>
              )}
            </CardTitle>
            {items.length > 0 && (
              <Button
                onClick={() => setConfirmAll(true)}
                disabled={rebilling}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="billing-audit-rebill-all"
              >
                <ReceiptText className="h-4 w-4 mr-1" /> Alle korrekt abrechnen
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500 py-8 text-center">Lädt…</p>
          ) : items.length === 0 ? (
            <div className="py-12 text-center" data-testid="billing-audit-empty">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
              <p className="text-gray-600 font-medium">Alles sauber abgerechnet! 🎉</p>
              <p className="text-sm text-gray-400">Keine Trainings ohne Rechnung/Punkte/Zahlung im gewählten Zeitraum.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-3">Datum</th>
                    <th className="py-2 pr-3">Kunde</th>
                    <th className="py-2 pr-3">Zahler</th>
                    <th className="py-2 pr-3">Training</th>
                    <th className="py-2 pr-3">Betrag</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3 text-right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.row_key || it.id} className="border-b last:border-0 hover:bg-amber-50/40" data-testid={`billing-audit-row-${it.row_key || it.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(it.date)}</td>
                      <td className="py-2 pr-3 font-medium">{it.customer_name}</td>
                      <td className="py-2 pr-3">
                        {it.payer_name
                          ? <Badge className="bg-emerald-100 text-emerald-700">💳 {it.payer_name}</Badge>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-2 pr-3">{it.service_name}</td>
                      <td className="py-2 pr-3 font-semibold whitespace-nowrap">{fmtChf(it.amount)}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-amber-700 border-amber-300">{it.billing_status}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={rebilling}
                          onClick={() => rebill([{ appointment_id: it.id, participant_id: it.participant_id }])}
                          data-testid={`billing-audit-rebill-${it.row_key || it.id}`}
                        >
                          Jetzt abrechnen
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-1"
                          disabled={rebilling}
                          onClick={() => setConfirmDelete(it)}
                          data-testid={`billing-audit-delete-${it.row_key || it.id}`}
                          title="Toten Termin löschen"
                        >
                          Löschen
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmAll} onOpenChange={setConfirmAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alle {items.length} Training(s) abrechnen?</AlertDialogTitle>
            <AlertDialogDescription>
              Für jedes Training wird eine Rechnung erstellt — bei aktiver Zahler-Beziehung
              landet sie automatisch beim Zahler. Gesamtbetrag: <strong>{fmtChf(total)}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => rebill(items.map(i => ({ appointment_id: i.id, participant_id: i.participant_id })))} data-testid="billing-audit-confirm-all">
              Ja, alle abrechnen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Toten Termin löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>Termin <strong>{confirmDelete.service_name}</strong> von <strong>{confirmDelete.customer_name}</strong> ({(confirmDelete.date || '').slice(0, 10)}) wird
                unwiderruflich gelöscht. Das ist nur für verwaiste/tote Termine gedacht — Termine mit
                echter Rechnung/Zahlung werden automatisch NICHT gelöscht.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => confirmDelete && deleteRows([confirmDelete.id])}
              data-testid="billing-audit-confirm-delete"
            >
              Ja, löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
